import React, { useState, useEffect, useContext } from 'react';
import { Button, Box, Modal, SpaceBetween, Alert } from '@amzn/awsui-components-react';
import { GlobalAppContext } from 'src/components/App';
import { getPermissions } from 'src/components/AppPermissions';
import { AppRoles } from 'src/models/permissions/RolePermissions';
import DateUtils from 'src/utils/dateUtils';
import CONSTANTS from 'src/utils/constants';
import {
    PromotionComponentType,
    PromotionWindowStatusResponse,
    getPromotionStageInfo,
    PROMOTION_COMPONENT_MODULE_MAP,
    WINDOW_STATUS_EXEMPT_COMPONENT_TYPES,
} from 'src/models/promotion/PromotionTypes';
import { PromotionConfirmationModal } from './PromotionConfirmationModal';
import { usePromotion } from './usePromotion';

interface DisabledReasonArgs {
    disabledReason?: string;
    canEdit: boolean;
    windowGateEnabled: boolean;
    windowStatus: PromotionWindowStatusResponse | null;
    isAdmin: boolean;
    windowStatusError: boolean;
}

function getDisabledReason(args: DisabledReasonArgs): string | undefined {
    const { disabledReason, canEdit, windowGateEnabled, windowStatus, isAdmin, windowStatusError } = args;
    if (disabledReason) return disabledReason;
    if (!canEdit) return 'You do not have permission to promote.';
    if (!windowGateEnabled) return undefined;
    if (windowStatus && !windowStatus.isOpen && !isAdmin) return 'Promotion window is currently closed.';
    if (windowStatusError) return 'Unable to verify promotion window status.';
    return undefined;
}

interface PromoteButtonProps {
    componentType: PromotionComponentType;
    componentId: string;
    componentName: string;
    componentVersion?: number;
    disabled?: boolean;
    disabledReason?: string;
    /**
     * Row-level promotion (Pattern B): the selected rows' promotion keys, sent in the promote
     * request. The count itself is surfaced by the Selected For Promotion table, not the button.
     */
    selectedRows?: string[];
    onPromotionComplete?: () => void;
    onPromotingStateChange?: (isPromoting: boolean) => void;
}

export function PromoteButton(props: PromoteButtonProps) {
    const {
        componentType, componentId, componentName, componentVersion,
        disabled, disabledReason, selectedRows, onPromotionComplete, onPromotingStateChange,
    } = props;

    const { services, userProfile } = useContext(GlobalAppContext);
    const [windowStatus, setWindowStatus] = useState<PromotionWindowStatusResponse | null>(null);
    const [windowStatusLoading, setWindowStatusLoading] = useState(true);
    const [windowStatusError, setWindowStatusError] = useState(false);
    const [overrideModalVisible, setOverrideModalVisible] = useState(false);

    const stageInfo = getPromotionStageInfo(CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE);
    const module = PROMOTION_COMPONENT_MODULE_MAP[componentType];
    const { canEdit } = getPermissions(module);
    const isAdmin = userProfile?.roles?.includes(AppRoles.ADMIN) ?? false;
    const windowGateEnabled = !WINDOW_STATUS_EXEMPT_COMPONENT_TYPES.has(componentType);

    const {
        isPromoting, lastPromotedAt, initializing, modalVisible,
        openModal, closeModal, startPromotion, featureEnabled
    } = usePromotion({ componentType, componentId, componentName, stageInfo, onPromotionComplete });

    useEffect(() => {
        if (!featureEnabled || !canEdit || !windowGateEnabled) {
            // no need to fetch window status anymore
            setWindowStatusLoading(false);
            return;
        }
        setWindowStatusLoading(true);
        setWindowStatusError(false);
        services.promotionService.getWindowStatus()
            .then(response => {
                setWindowStatus(response);
            })
            .catch((error: any) => {
                setWindowStatusError(true);
                services.messageService.showErrorBanner(
                    error?.message || 'Failed to load promotion window status. Please try again later.'
                );
            })
            .finally(() => {
                setWindowStatusLoading(false);
            });
    }, [featureEnabled, canEdit]);

    useEffect(() => {
        if (onPromotingStateChange) {
            onPromotingStateChange(isPromoting);
        }
    }, [isPromoting, onPromotingStateChange]);

    if (featureEnabled === null || featureEnabled === false || !stageInfo) {
        return null;
    }

    const effectiveDisabledReason = getDisabledReason({
        disabledReason, canEdit, windowGateEnabled, windowStatus, isAdmin, windowStatusError,
    });

    const isPromotionLoading = isPromoting;
    const isInitLoading = initializing || windowStatusLoading;
    const buttonLoading = isPromotionLoading || isInitLoading;
    const buttonDisabled = disabled || !!effectiveDisabledReason || buttonLoading || modalVisible;
    const buttonText = isPromoting ? `Promoting to ${stageInfo.to}` : `Promote to ${stageInfo.to}`;

    const handlePromoteClick = () => {
        if (!windowGateEnabled) {
            openModal();
            return;
        }
        if (!windowStatus) return;
        if (!windowStatus.isOpen && isAdmin) {
            setOverrideModalVisible(true);
        } else {
            openModal();
        }
    };

    const handleOverrideConfirm = () => {
        setOverrideModalVisible(false);
        openModal();
    };

    const handleOverrideDismiss = () => {
        setOverrideModalVisible(false);
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {lastPromotedAt && (
                    <Box variant="p" color="text-body-secondary">
                        Last promoted at {DateUtils.formatDate(lastPromotedAt, 'YYYY-MM-DD HH:mm')}
                    </Box>
                )}
                <Button
                    variant="primary"
                    onClick={handlePromoteClick}
                    disabled={buttonDisabled}
                    disabledReason={effectiveDisabledReason}
                    loading={buttonLoading}
                >
                    {buttonText}
                </Button>
            </div>

            <Modal
                visible={overrideModalVisible}
                onDismiss={handleOverrideDismiss}
                header={`Promote to ${stageInfo.to}`}
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={handleOverrideDismiss}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleOverrideConfirm}>
                                Override and continue
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <Alert type="warning">
                    The promotion window is currently closed. Do you still want to continue?
                </Alert>
            </Modal>

            <PromotionConfirmationModal
                visible={modalVisible}
                componentName={componentName}
                componentType={componentType}
                componentId={componentId}
                componentVersion={componentVersion}
                stageInfo={stageInfo}
                onDismiss={closeModal}
                onPromotionStarted={startPromotion}
                selectedRows={selectedRows}
            />
        </>
    );
}
